import React, { useState, useEffect } from 'react';
import { ScrollView, StyleSheet, View, SafeAreaView } from 'react-native';
import Button, { BUTTON_TYPES } from '../Button';
import InputForm from '../InputForm';
import PickerSelect from '../PickerSelect';
import SwitchWithLabel from '../SwitchWithLabel';
import VerticalSelect from '../VerticalSelect';
import { CommonProperties, Color, CommonStyles } from '../../styles';
import { hasRegisteredStates, getStates } from '../../utils/country.utils';
import { deleteCreditCard, saveCreditCard } from '../../services/api/profile.service';
import BaseText from '../BaseText';
import RowView from '../RowView';
import BottomButton from '../BottomButton';
import MessageAlert from '../MessageAlert';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../../i18n';
import useTextMonths from '../../utils/useTextMonths';
import { useDispatch, useSelector } from 'react-redux';
import { openAlert } from '../../store/actions/alert';
import { validateEmail } from '../../utils/common.utils';
import { selectHomeAddress, selectWorkAddress } from '../../store/selectors/profile.selector';
import { getCreditCardVendor } from '../../utils/creditcard.util';

const BillingAddressType = {
  PERSONAL_ADDRESS: 1,
  COMPANY_ADDRESS: 2,
  OTHER_ADDRESS: 3,
};

const actualYear = new Date().getFullYear();
const years = Array.from({ length: 10 }, (v, k) => ({
  label: (actualYear + k).toString(),
  value: actualYear + k,
}));

const getCreditCardLastFourDigits = (creditCardNumber) => {
  const creditCardStr = String(creditCardNumber).trim();
  const lastFourDigits = creditCardStr.slice(-4);
  return lastFourDigits;
};

const maskCreditCardNumber = (creditCardNumber) => {
  const str = creditCardNumber.toString();
  const lastFourDigits = str.slice(-4);
  // Mask the rest of the digits
  const maskedNumber = 'XXXX XXXX XXXX ' + lastFourDigits;
  return maskedNumber;
};

const mapAddress = (address) => ({
  Line1: address?.Line1,
  Line2: address?.Line2,
  City: address?.City,
  State: address?.State,
  StateName: address?.StateName,
  Country: address?.Country,
  Zip: address?.Zip,
});
const CreditCardForm = ({
  countryList = [],
  creditCard = {},
  creditCardTypes,
  creditCardProviders,
  onSubmit,
  onDelete,
}) => {
  const dispatch = useDispatch();
  const { tKey } = useComponentTKey('CreditCardForm');
  const { t } = useTranslation(i18nNS.me);
  const workAddress = useSelector(selectWorkAddress);
  const homeAddress = useSelector(selectHomeAddress);
  const [creditCardKey, setCreditCardKey] = useState(0);
  const [nickname, setNickName] = useState('');
  const [number, setNumber] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [creditCardProvider, setCreditCardProvider] = useState('');
  const [creditCardType, setCreditCardType] = useState('');

  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [useCardAir, setUseCardAir] = useState(false);
  const [useCardHotel, setUseCardHotel] = useState(false);
  const [useCardCar, setUseCardCar] = useState(false);
  const [useCardExtra, setUseCardExtra] = useState(false);
  const [optionBillingAddress, setOptionBillingAddress] = useState(null);
  const [cardHolderEmail, setCardHolderEmail] = useState('');

  const [billingAddress, setBillingAddress] = useState('');
  const [address2, setAddress2] = useState('');
  const [countryBilling, setCountryBilling] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [loading, setLoading] = useState(false);
  const [isValidError, setIsValidError] = useState(false);

  const [stateList, setStateList] = useState([]);

  const [errorMessages, setErrorMessages] = useState({});
  const [months] = useTextMonths();
  let scrollView = null;

  const isEditCreditCard =
    creditCard &&
    Object.keys(creditCard).length > 0 &&
    creditCard.CreditCardNumber &&
    creditCard.CreditCardNumber.trim().length > 0;
  const isHomeAddressIncomplete = (address) => {
    return (
      !address?.Line1 ||
      !address?.Line2 ||
      !address?.City ||
      !address?.State ||
      !address?.StateName ||
      !address?.Country ||
      !address?.Zip
    );
  };
  const billingOptions = [
    !isHomeAddressIncomplete(homeAddress) && {
      label: t(tKey('PersonalAddress')),
      value: BillingAddressType.PERSONAL_ADDRESS,
    },
    {
      label: t(tKey('CompanyAddress')),
      value: BillingAddressType.COMPANY_ADDRESS,
    },
    {
      label: t(tKey('OtherAddress')),
      value: BillingAddressType.OTHER_ADDRESS,
    },
  ].filter(Boolean);

  const fillStates = async (_countryCode) => {
    if (hasRegisteredStates(_countryCode)) {
      const res = await getStates(_countryCode);
      setStateList(res);
    } else {
      setState('');
    }
  };

  useEffect(() => {
    fillStates(countryBilling);
  }, [countryBilling]);

  useEffect(() => {
    if (creditCard && Object.keys(creditCard).length > 0) {
      console.log(creditCard);
      setCreditCardKey(creditCard.CreditCardKey);
      setCreditCardProvider(creditCard.CreditCardVendorCode);
      setCreditCardType(creditCard.CreditCardType);
      setNickName(creditCard.CreditCardDescription);
      setNumber(creditCard.CreditCardNumber);
      setMonth(creditCard.CreditCardExpiryMonth.toString());
      setYear(creditCard.CreditCardExpiryYear);
      setName(creditCard.CreditCardName);
      setUseCardAir(creditCard.isAir);
      setUseCardHotel(creditCard.isHotel);
      setUseCardCar(creditCard.isCar);
      setUseCardExtra(creditCard.isAirExtra);
      setCardHolderEmail(creditCard.CardHolderEmail);

      if (
        creditCard.BillingAddressType === BillingAddressType.PERSONAL_ADDRESS &&
        isHomeAddressIncomplete
      ) {
        setOptionBillingAddress(BillingAddressType.OTHER_ADDRESS);
      } else {
        setOptionBillingAddress(creditCard.BillingAddressType);
      }

      if (creditCard.BillingAddress) {
        setCity(creditCard.BillingAddress.City);
        setBillingAddress(creditCard.BillingAddress.Line1);
        setAddress2(creditCard.BillingAddress.Line2);
        setCountryBilling(creditCard.BillingAddress.Country);
        setZip(creditCard.BillingAddress.Zip);

        if (hasRegisteredStates(creditCard.BillingAddress.Country)) {
          setState(creditCard.BillingAddress.State);
        } else {
          setState(creditCard.BillingAddress.StateName);
        }
      }
    } else {
      console.log(creditCard);
    }
  }, [creditCard]);

  const isValid = () => {
    const errorMessages = {};

    // Helper function to check if a field is empty and set an error message
    const checkFieldEmpty = (field, fieldName, errorKey) => {
      if (!field) {
        errorMessages[fieldName] = t(`common:Error.${errorKey}`);
      }
    };

    // Field validations
    checkFieldEmpty(number, 'number', 'requiredCreditCardNumber');
    checkFieldEmpty(nickname, 'nickname', 'requiredNickname');
    checkFieldEmpty(name, 'name', 'requireFirstLastName');
    checkFieldEmpty(month, 'month', 'requiredMonth');
    checkFieldEmpty(year, 'year', 'requiredYear');
    checkFieldEmpty(creditCardProvider, 'creditCardProvider', 'requiredCreditCardProvider');
    checkFieldEmpty(creditCardType, 'creditCardType', 'requiredCreditCardType');
    checkFieldEmpty(cardHolderEmail, 'cardHolderEmail', 'requiredCardHolderEmail');

    // Specific validations
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    if (month && year && new Date(year, month, 0) < currentDate) {
      if (currentYear > year) {
        errorMessages.year = t('common:Error.greaterExpiryYear');
      } else if (currentYear == year && currentMonth > month) {
        errorMessages.month = t('common:Error.greaterExpiryMonth');
      }
    }

    if (number && getCreditCardVendor(number) == null) {
      errorMessages.number = t('common:Error.invalidCard');
    }
    if (cardHolderEmail && !validateEmail(cardHolderEmail)) {
      errorMessages.cardHolderEmail = t('common:Error.invalidCardHolderEmail');
    }

    if (optionBillingAddress === BillingAddressType.OTHER_ADDRESS) {
      checkFieldEmpty(billingAddress, 'billingAddress', 'requiredBillingAddress');
      checkFieldEmpty(city, 'city', 'requiredCity');
      checkFieldEmpty(countryBilling, 'country', 'requiredCountry');
      checkFieldEmpty(state, 'state', 'requiredState');
      checkFieldEmpty(zip, 'zip', 'requiredZipCode');
    }

    setErrorMessages(errorMessages);

    return Object.keys(errorMessages).length === 0;
  };

  const onPressSubmit = async () => {
    setLoading(true);
    if (!isValid()) {
      setLoading(false);
      setIsValidError(true);
      setTimeout(() => {
        setIsValidError(false);
      }, 5000);
      return;
    }

    //build address
    let creditCardAddress = {};
    switch (optionBillingAddress) {
      case BillingAddressType.COMPANY_ADDRESS:
        creditCardAddress = mapAddress(workAddress);
        break;
      case BillingAddressType.PERSONAL_ADDRESS:
        creditCardAddress = mapAddress(homeAddress);
        break;
      default:
        creditCardAddress = {
          Line1: billingAddress,
          Line2: address2,
          City: city,
          Country: countryBilling,
          Zip: zip,
        };
        break;
    }

    //set state based on country
    if (optionBillingAddress === BillingAddressType.OTHER_ADDRESS) {
      if (state && hasRegisteredStates(countryBilling)) {
        const _state = stateList.find((i) => state === i.value);
        creditCardAddress.State = state;
        creditCardAddress.StateName = _state.label;
      } else {
        creditCardAddress.StateName = state;
      }
    }

    const data = {
      CreditCardKey: creditCardKey,
      CreditCardVendorCode: creditCardProvider,
      CreditCardType: creditCardType,
      CreditCardExpiryMonth: month,
      CardHolderEmail: cardHolderEmail,
      BillingAddress: creditCardAddress,
      CreditCardName: name,
      CreditCardAccount: '',
      CreditCardExpiryDate: new Date(year, month, 0),
      CreditCardDescription: nickname,
      isAir: useCardAir,
      isAirExtra: useCardExtra,
      isHotel: useCardHotel,
      isCar: useCardCar,
      IsRail: false,
      CreditCardNumber: number,
      CreditCardExpiryYear: year,
      BillingAddressType: optionBillingAddress,
      CreditCardLastFourDigit: getCreditCardLastFourDigits(number),
    };

    try {
      await saveCreditCard(data);
      onSubmit(creditCardKey);
      dispatch(openAlert({ message: t('common:Error.creditCardUpdated') }));
    } catch (error) {
      console.error('Error saving credit card:', error);
    } finally {
      setLoading(false);
    }
  };

  const onPressDelete = async () => {
    try {
      await deleteCreditCard(creditCard);
      onDelete(creditCardKey);
      dispatch(openAlert({ message: t('common:Error.creditCardRemoved') }));
    } catch (error) {
      console.error('Error saving credit card:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={CommonStyles.flex1}>
      <ScrollView
        ref={(ref) => (scrollView = ref)}
        onContentSizeChange={() => {
          if (optionBillingAddress === 3) {
            scrollView?.scrollToEnd({ animated: true });
          }
        }}
      >
        <View style={styles.body}>
          <InputForm
            label={t(tKey('CardNickname'))}
            value={nickname}
            errorMessage={errorMessages.nickname}
            onChangeText={setNickName}
          />
          <InputForm
            label={t(tKey('CardHolderEmail'))}
            value={cardHolderEmail}
            errorMessage={errorMessages.cardHolderEmail}
            onChangeText={setCardHolderEmail}
          />
          <BaseText style={CommonStyles.mb2}>{t(tKey('CardProvider'))}</BaseText>
          <RowView>
            <PickerSelect
              onValueChange={(value) => {
                setCreditCardProvider(value);
              }}
              items={creditCardProviders}
              value={creditCardProvider}
              errorMessage={errorMessages.creditCardProvider}
              placeholder={{ label: t(tKey('CardProvider')) }}
            />
          </RowView>
          <BaseText style={CommonStyles.mb2}>{t(tKey('CardType'))}</BaseText>
          <RowView>
            <PickerSelect
              onValueChange={(value) => {
                setCreditCardType(value);
              }}
              items={creditCardTypes}
              value={creditCardType}
              errorMessage={errorMessages.creditCardType}
              placeholder={{ label: t(tKey('CardType')) }}
            />
          </RowView>
          <InputForm
            label={t(tKey('CreditcardNumber'))}
            value={isEditCreditCard ? maskCreditCardNumber(number) : number}
            editable={isEditCreditCard ? false : true}
            errorMessage={errorMessages.number}
            keyboardType="numeric"
            placeholder="XXXX XXXX XXXX XXXX"
            onChangeText={setNumber}
          />
          <BaseText style={CommonStyles.mb2}>{t(tKey('CardExpiration'))}</BaseText>
          <RowView>
            <PickerSelect
              onValueChange={(value) => {
                setMonth(value.toString());
              }}
              items={months}
              value={month}
              errorMessage={errorMessages.month}
              placeholder={{ label: t(tKey('SelectMonth')) }}
            />
            <PickerSelect
              style={CommonStyles.ml3}
              onValueChange={(value) => {
                setYear(value);
              }}
              items={years}
              value={year}
              errorMessage={errorMessages.year}
              placeholder={{ label: t(tKey('SelectYear')) }}
            />
          </RowView>
          <InputForm
            label={t(tKey('CardName'))}
            value={name}
            editable={isEditCreditCard ? false : true}
            errorMessage={errorMessages.name}
            onChangeText={setName}
          />

          <BaseText style={CommonStyles.mb2}>{t(tKey('UseCardFor'))}:</BaseText>
          <View style={[styles.checkContainer, CommonStyles.px3]}>
            <SwitchWithLabel label={t('common:Air')} onChange={setUseCardAir} value={useCardAir} />
            <SwitchWithLabel
              label={t('common:Hotel')}
              onChange={setUseCardHotel}
              value={useCardHotel}
            />
            <SwitchWithLabel label={t('common:Car')} onChange={setUseCardCar} value={useCardCar} />
            <SwitchWithLabel
              label={t(tKey('SeatsExtras'))}
              onChange={setUseCardExtra}
              value={useCardExtra}
              noBorder={true}
            />
          </View>
          <BaseText style={CommonStyles.mb2}>{t(tKey('BillingAddress'))}</BaseText>
          <VerticalSelect
            style={styles.checkContainer}
            onChange={setOptionBillingAddress}
            selected={optionBillingAddress}
            items={billingOptions}
          />
          {optionBillingAddress === 3 && (
            <View>
              <InputForm
                label={t(tKey('BillingAddress'))}
                value={billingAddress}
                onChangeText={setBillingAddress}
                errorMessage={errorMessages.billingAddress}
              />
              <InputForm label={t(tKey('Address2'))} value={address2} onChangeText={setAddress2} />
              <InputForm
                label={t(tKey('City'))}
                value={city}
                errorMessage={errorMessages.city}
                onChangeText={setCity}
              />
              <PickerSelect
                label={t(tKey('CountryOfBillingAddress'))}
                onValueChange={(value) => {
                  setCountryBilling(value);
                }}
                items={countryList}
                value={countryBilling}
                placeholder={{ label: t(tKey('SelectCountry')) }}
                errorMessage={errorMessages.country}
              />
              {hasRegisteredStates(countryBilling) ? (
                <>
                  <PickerSelect
                    label="State"
                    onValueChange={(value) => {
                      setState(value);
                    }}
                    items={stateList}
                    value={state}
                    placeholder={{ label: t(tKey('SelectState')) }}
                    errorMessage={errorMessages.state}
                  />
                </>
              ) : (
                <InputForm
                  label={t(tKey('State'))}
                  value={state}
                  onChangeText={setState}
                  errorMessage={errorMessages.state}
                />
              )}
              <InputForm
                label={t(tKey('Zip'))}
                value={zip}
                maxLength={5}
                keyboardType="numeric"
                onChangeText={setZip}
                errorMessage={errorMessages.zip}
              />
            </View>
          )}
          {creditCardKey !== 0 && (
            <Button
              type={BUTTON_TYPES.OUTLINED}
              color={Color.required}
              style={[CommonStyles.py3, CommonStyles.borderRadius4]}
              labelStyle={CommonStyles.h4}
              onPress={onPressDelete}
              label={t(tKey('DeleteCard'))}
            />
          )}
        </View>
      </ScrollView>
      <BottomButton
        onPress={onPressSubmit}
        loading={loading}
        label={(creditCardKey === 0 ? t('common:Save') : t(tKey('Update'))) + t(tKey('Card'))}
      >
        {isValidError && <MessageAlert message={t(tKey('RequiredFields'))} />}
      </BottomButton>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  body: {
    ...CommonProperties.px3,
  },
  checkContainer: {
    backgroundColor: 'rgb(250,250,250)',
    ...CommonProperties.mt2,
    ...CommonProperties.mb3,
    ...CommonProperties.borderRadius,
    ...CommonProperties.shadow,
  },
});

export default CreditCardForm;
